import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const locationsCollection = 'locations';

// Get all locations
export const getLocations = async () => {
  try {
    const locationsRef = collection(db, locationsCollection);
    const q = query(locationsRef, orderBy('city', 'asc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    }));
  } catch (error) {
    console.error('Error fetching locations:', error);
    throw new Error('Failed to fetch locations');
  }
};

// Get only active locations (for checkout)
export const getActiveLocations = async () => {
  try {
    const locationsRef = collection(db, locationsCollection);
    
    // Try the optimized query with composite index first
    try {
      const q = query(
        locationsRef, 
        where('isActive', '==', true),
        orderBy('city', 'asc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (indexError) {
      // Fallback: If composite index is still building, use simpler query and sort in memory
      console.warn('Composite index still building, using fallback query:', indexError.message);
      
      const fallbackQuery = query(
        locationsRef, 
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(fallbackQuery);
      
      const locations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
      
      // Sort in memory as fallback
      return locations.sort((a, b) => (a.city || '').localeCompare(b.city || ''));
    }
  } catch (error) {
    console.error('Error fetching active locations:', error);
    throw new Error('Failed to fetch active locations');
  }
};

// Create a new location
export const createLocation = async (locationData) => {
  try {
    const newLocation = {
      ...locationData,
      isActive: locationData.isActive ?? true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, locationsCollection), newLocation);
    
    return {
      id: docRef.id,
      ...newLocation,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error creating location:', error);
    throw new Error('Failed to create location');
  }
};

// Update an existing location
export const updateLocation = async (id, locationData) => {
  try {
    const locationRef = doc(db, locationsCollection, id);
    const updateData = {
      ...locationData,
      updatedAt: serverTimestamp()
    };

    await updateDoc(locationRef, updateData);
    
    return {
      id,
      ...updateData,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error updating location:', error);
    throw new Error('Failed to update location');
  }
};

// Delete a location
export const deleteLocation = async (id) => {
  try {
    const locationRef = doc(db, locationsCollection, id);
    await deleteDoc(locationRef);
    return { id };
  } catch (error) {
    console.error('Error deleting location:', error);
    throw new Error('Failed to delete location');
  }
};

// Toggle location active status
export const toggleLocationStatus = async (id, isActive) => {
  try {
    const locationRef = doc(db, locationsCollection, id);
    await updateDoc(locationRef, {
      isActive,
      updatedAt: serverTimestamp()
    });
    
    return { id, isActive, updatedAt: new Date() };
  } catch (error) {
    console.error('Error toggling location status:', error);
    throw new Error('Failed to toggle location status');
  }
}; 